const TAB_ID_KEY = "materialentnahme.pwa.tabId";
const LEASE_KEY = "materialentnahme.appUpdate.coordinatorLease";
const SIGNAL_KEY = "materialentnahme.appUpdate.coordinationSignal";
const CHANNEL_NAME = "materialentnahme-pwa-update-v1";
export const UPDATE_COORDINATION_LEASE_MS = 45_000;

export type UpdateCoordinationEventType = "applying" | "activated" | "failed" | "released";

export interface UpdateCoordinationEvent {
  type: UpdateCoordinationEventType;
  tabId: string;
  targetVersion: string | null;
  at: string;
  detail?: string | null;
}

interface UpdateLease {
  tabId: string;
  nonce: string;
  targetVersion: string;
  expiresAt: number;
}

function randomToken(): string {
  try { return crypto.randomUUID(); }
  catch { return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`; }
}

function storageGet(storage: Storage, key: string): string | null {
  try { return storage.getItem(key); } catch { return null; }
}

function storageSet(storage: Storage, key: string, value: string): boolean {
  try { storage.setItem(key, value); return true; } catch { return false; }
}

function storageRemove(storage: Storage, key: string): void {
  try { storage.removeItem(key); } catch { /* coordination is best effort */ }
}

export function getPwaTabId(): string {
  const existing = storageGet(window.sessionStorage, TAB_ID_KEY);
  if (existing) return existing;
  const created = randomToken();
  storageSet(window.sessionStorage, TAB_ID_KEY, created);
  return created;
}

function readLease(): UpdateLease | null {
  const raw = storageGet(window.localStorage, LEASE_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<UpdateLease>;
    if (!parsed.tabId || !parsed.nonce || !parsed.targetVersion || !Number.isFinite(parsed.expiresAt)) return null;
    return parsed as UpdateLease;
  } catch { return null; }
}

export interface UpdateLeaseHandle {
  owned: boolean;
  ownerTabId: string | null;
  targetVersion: string;
  renew: () => boolean;
  release: () => void;
}

export function tryAcquireUpdateLease(targetVersion: string): UpdateLeaseHandle {
  const tabId = getPwaTabId();
  const now = Date.now();
  const current = readLease();
  if (current && current.expiresAt > now && current.tabId !== tabId) {
    return { owned: false, ownerTabId: current.tabId, targetVersion, renew: () => false, release: () => undefined };
  }

  const nonce = randomToken();
  const candidate: UpdateLease = { tabId, nonce, targetVersion, expiresAt: now + UPDATE_COORDINATION_LEASE_MS };
  if (!storageSet(window.localStorage, LEASE_KEY, JSON.stringify(candidate))) {
    // localStorage can be unavailable in hardened/private contexts. We deliberately
    // fail open to the existing per-tab single-flight rather than making updates impossible.
    return { owned: true, ownerTabId: tabId, targetVersion, renew: () => true, release: () => undefined };
  }

  const confirmed = readLease();
  const owned = Boolean(confirmed && confirmed.tabId === tabId && confirmed.nonce === nonce);
  const renew = () => {
    const lease = readLease();
    if (!lease || lease.tabId !== tabId || lease.nonce !== nonce) return false;
    lease.expiresAt = Date.now() + UPDATE_COORDINATION_LEASE_MS;
    return storageSet(window.localStorage, LEASE_KEY, JSON.stringify(lease));
  };
  const release = () => {
    const lease = readLease();
    if (lease?.tabId === tabId && lease.nonce === nonce) storageRemove(window.localStorage, LEASE_KEY);
  };
  return { owned, ownerTabId: confirmed?.tabId ?? null, targetVersion, renew, release };
}

function parseEvent(value: unknown): UpdateCoordinationEvent | null {
  if (!value || typeof value !== "object") return null;
  const candidate = value as Partial<UpdateCoordinationEvent>;
  if (!candidate.type || !["applying", "activated", "failed", "released"].includes(candidate.type)) return null;
  if (!candidate.tabId || !candidate.at) return null;
  return {
    type: candidate.type,
    tabId: candidate.tabId,
    targetVersion: typeof candidate.targetVersion === "string" ? candidate.targetVersion : null,
    at: candidate.at,
    detail: typeof candidate.detail === "string" ? candidate.detail : null,
  };
}

export function publishUpdateCoordination(type: UpdateCoordinationEventType, targetVersion: string | null, detail?: string): void {
  const event: UpdateCoordinationEvent = { type, tabId: getPwaTabId(), targetVersion, at: new Date().toISOString(), detail: detail ?? null };
  if (typeof BroadcastChannel !== "undefined") {
    try {
      const channel = new BroadcastChannel(CHANNEL_NAME);
      channel.postMessage(event);
      channel.close();
    } catch { /* storage signal remains available */ }
  }
  storageSet(window.localStorage, SIGNAL_KEY, JSON.stringify({ ...event, nonce: randomToken() }));
}

export function subscribeUpdateCoordination(listener: (event: UpdateCoordinationEvent) => void): () => void {
  const ownTabId = getPwaTabId();
  let channel: BroadcastChannel | null = null;
  const deliver = (value: unknown) => {
    const event = parseEvent(value);
    if (!event || event.tabId === ownTabId) return;
    listener(event);
  };

  if (typeof BroadcastChannel !== "undefined") {
    try {
      channel = new BroadcastChannel(CHANNEL_NAME);
      channel.addEventListener("message", (event: MessageEvent<unknown>) => deliver(event.data));
    } catch { channel = null; }
  }

  const onStorage = (event: StorageEvent) => {
    if (event.key !== SIGNAL_KEY || !event.newValue) return;
    try { deliver(JSON.parse(event.newValue) as unknown); } catch { /* ignore malformed foreign signal */ }
  };
  window.addEventListener("storage", onStorage);
  return () => {
    window.removeEventListener("storage", onStorage);
    try { channel?.close(); } catch { /* ignore */ }
  };
}
